import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircleCheckBig, CircleX } from 'lucide-react';
import { CharacterCounter } from '@/components/common/CharacterCounter';
import { ConfirmDialog, type ConfirmDialogVariant } from '@/components/common/ConfirmDialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { REVIEW_COMMENT_MAX, REVIEW_DECISION } from '../constants/leaveApplication.constants';
import { useReviewLeaveApplication } from '../hooks/useReviewLeaveApplication';
import {
  reviewLeaveApplicationSchema,
  type ReviewDecision,
  type ReviewLeaveApplicationPayload,
} from '../types/leaveApplication.types';

interface ConfirmCopy {
  title: string;
  description: string;
  confirmLabel: string;
  pendingLabel: string;
  variant: ConfirmDialogVariant;
}

const CONFIRM_COPY: Record<ReviewDecision, ConfirmCopy> = {
  APPROVE: {
    title: 'Approve this leave application?',
    description: 'Once approved, this request can no longer be changed.',
    confirmLabel: 'Approve',
    pendingLabel: 'Approving...',
    variant: 'default',
  },
  REJECT: {
    title: 'Reject this leave application?',
    description: 'Once rejected, this request can no longer be changed.',
    confirmLabel: 'Reject',
    pendingLabel: 'Rejecting...',
    variant: 'destructive',
  },
};

interface PendingReview {
  decision: ReviewDecision;
  payload: ReviewLeaveApplicationPayload;
}

interface LeaveApplicationReviewFormProps {
  applicationId: number;
  disabled: boolean;
  onDirtyChange: (dirty: boolean) => void;
}

export function LeaveApplicationReviewForm({
  applicationId,
  disabled,
  onDirtyChange,
}: LeaveApplicationReviewFormProps) {
  const form = useForm<ReviewLeaveApplicationPayload>({
    resolver: zodResolver(reviewLeaveApplicationSchema),
    defaultValues: { comment: '' },
  });

  const [pendingReview, setPendingReview] = useState<PendingReview | null>(null);
  const reviewMutation = useReviewLeaveApplication(() => form.reset());
  const commentLength = form.watch('comment').length;
  const isSubmitting = reviewMutation.isPending || form.formState.isSubmitting;
  const { isDirty } = form.formState;

  useEffect(() => {
    onDirtyChange(isDirty);
    return () => onDirtyChange(false);
  }, [isDirty, onDirtyChange]);

  function requestReview(decision: ReviewDecision) {
    if (isSubmitting) return;
    void form.handleSubmit((payload) => {
      setPendingReview({ decision, payload });
    })();
  }

  function confirmReview() {
    if (!pendingReview) return;
    reviewMutation.mutate(
      { id: applicationId, ...pendingReview },
      { onSettled: () => setPendingReview(null) },
    );
  }

  const confirmCopy = pendingReview ? CONFIRM_COPY[pendingReview.decision] : null;

  return (
    <Form {...form}>
      <div className="flex flex-1 flex-col gap-3">
        <FormField
          control={form.control}
          name="comment"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel className="gap-0.5">
                  Your Comment<span className="text-destructive">*</span>
                </FormLabel>
                <CharacterCounter current={commentLength} max={REVIEW_COMMENT_MAX} />
              </div>
              <FormControl>
                <Textarea
                  {...field}
                  maxLength={REVIEW_COMMENT_MAX}
                  placeholder="Enter your comment here..."
                  disabled={isSubmitting}
                  className="field-sizing-fixed min-h-40 resize-y overflow-y-auto"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="mt-auto flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="destructive"
            disabled={disabled || isSubmitting}
            onClick={() => requestReview(REVIEW_DECISION.REJECT)}
          >
            <CircleX />
            Reject
          </Button>
          <Button
            type="button"
            disabled={disabled || isSubmitting}
            onClick={() => requestReview(REVIEW_DECISION.APPROVE)}
          >
            <CircleCheckBig />
            Approve
          </Button>
        </div>
      </div>

      {confirmCopy && (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next && !reviewMutation.isPending) setPendingReview(null);
          }}
          title={confirmCopy.title}
          description={confirmCopy.description}
          confirmLabel={confirmCopy.confirmLabel}
          pendingLabel={confirmCopy.pendingLabel}
          confirmVariant={confirmCopy.variant}
          isPending={reviewMutation.isPending}
          onConfirm={confirmReview}
        />
      )}
    </Form>
  );
}
